import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  useClubAdminCancelMemberRoleMutation,
  useClubAdminUpdateMemberSpeechMutation,
  useGetMeetingDetailsQuery,
  useUpdateMeetingAgendaMutation,
  useUpdateSpeechMutation,
} from '../../../features/SpeechAgendaApiSlice.ts'
import { MeetingDetailsRequest } from '../../../types/Types.ts'
import { useSetTitle } from '../../../app/hooks/UseSetTitle.ts'
import { useAppAuth } from '../../../app/hooks/UseAppAuth.ts'
import SimpleModalDialog from '../../../components/modals/SimpleModalDialog.tsx'
import MeetingDetailsSkeleton from '../../../components/skeletons/MeetingDetailsSkeleton.tsx'
import SignUpModal, {
  RegistrationStep,
} from '../../../components/modals/SignUpModal.tsx'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../app/ReduxStore.ts'
import { clearWelcomeMessage } from '../../../features/SignUpWelcomeMessageSlice.ts'
import { useAssignRole } from '../../../app/hooks/UseAssignRole.ts'
import { useCancelRole } from '../../../app/hooks/UseCancelRole.ts'
import { usePathways } from '../../../app/hooks/UsePathways.ts'
import SpeechDetailsDialog from '../../../components/modals/SpeechDetailsDialog.tsx'
import ConfirmActionDialog from '../../../components/modals/ConfirmActionDialog.tsx'
import MeetingAgendaSection from '../../../components/club/MeetingAgendaSection.tsx'
import AdminRoleAssignment from '../../../components/club/AdminRoleAssignment.tsx'
import ManageClubButton from '../../../components/club/ManageClubButton.tsx'
import {
  ADMIN_ACCESS_ROLE_ID,
  SPEAKER_ROLE_ID,
  SpeechDetails,
} from '../../../types/Types.ts'
import { AssignedUser, BaseResponse, UpdateMeetingAgendaRequest } from '../../../generated'
import {
  formatAdminRoleCancellationResponse,
  parseErrorResponse,
} from '../../../utils/FormatterUtils.ts'

export default function MeetingDetails() {
  const { userLoaded } = useAppAuth()

  const dispatch = useDispatch()
  const welcomeMessage = useSelector((state: RootState) => state.signUpWelcome)

  const navigate = useNavigate()

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalSuccess, setModalSuccess] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [modalDescription, setModalDescription] = useState('')

  const [signUpModalOpen, setSignUpModalOpen] = useState(false)
  const [speechModalOpen, setSpeechModalOpen] = useState(false)
  const [editSpeechOpen, setEditSpeechOpen] = useState(false)
  const [editingSpeech, setEditingSpeech] = useState<SpeechDetails | null>(null)
  const [editSpeechTarget, setEditSpeechTarget] = useState<AssignedUser | null>(
    null
  )
  const [finishSignUpModalOpen, setFinishSignUpModalOpen] = useState(
    welcomeMessage.present
  )

  const { clubId, meetingTimestampUtc } = useParams()
  const { user: currentUser, isAuthenticated } = useAppAuth()

  // Only club members may edit the agenda — the backend enforces this with a
  // 403, so mirror it in the UI to keep non-members read-only (a signed-in user
  // viewing another club's public meeting should not see the edit affordance).
  const isClubMember =
    isAuthenticated &&
    !!currentUser?.clubs?.some((club) => club.id === Number(clubId))

  const isClubAdmin =
    isAuthenticated &&
    currentUser?.clubs?.find((club) => club.id === Number(clubId))?.roleId ===
      ADMIN_ACCESS_ROLE_ID

  const showResult = (success: boolean, title: string, description: string) => {
    setModalSuccess(success)
    setModalTitle(title)
    setModalDescription(description)
    setModalOpen(true)
  }

  const { groups: pathwayGroups, labelForCode } = usePathways()

  //Meeting Info
  const apiRequest: MeetingDetailsRequest = {
    clubId: clubId || '',
    meetingTimestampUtc: meetingTimestampUtc || '',
  }

  const {
    data: meetingDetails,
    isLoading,
    isFetching,
  } = useGetMeetingDetailsQuery(apiRequest, {
    skip: !clubId || !meetingTimestampUtc || !userLoaded,
  })

  useSetTitle(meetingDetails?.meetingName || null)

  const [roleSelected, setRoleSelected] = useState(false)
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
  const [selectedLinkedRoleId, setSelectedLinkedRoleId] = useState<
    number | undefined
  >(undefined)

  //Save Role Selection
  const handleSaveRoleSelection = (roleId: number, linkedRoleId?: number) => {
    setRoleSelected(true)
    setSelectedRoleId(roleId)
    setSelectedLinkedRoleId(linkedRoleId || undefined)
  }

  const roleId = isAuthenticated
    ? selectedRoleId
    : //Assign guest role if user is not authenticated, back-end will
      //always return an only one guest role for unauthorized users
      meetingDetails?.roles?.[0]?.id
  const [assignRoleData, assignRoleState, assignRole] = useAssignRole(
    roleId,
    clubId,
    meetingTimestampUtc,
    selectedLinkedRoleId
  )

  //Schedule Role
  const handleRoleAssignment = async () => {
    if (!isAuthenticated) {
      setSignUpModalOpen(true)
      return
    }
    // Speaker bookings capture speech details first; everything else books now.
    if (selectedRoleId === SPEAKER_ROLE_ID) {
      setSpeechModalOpen(true)
      return
    }
    await assignRole()
  }

  //Confirm Speaker speech details, then book the role
  const handleSpeechSubmit = async (speechDetails: SpeechDetails) => {
    await assignRole(speechDetails)
  }

  useEffect(() => {
    if (assignRoleData) {
      setModalTitle(assignRoleData?.title || '')
      setModalDescription(assignRoleData?.description || '')
    }
    if (assignRoleState === 'SUCCESS') {
      setSpeechModalOpen(false)
      setModalSuccess(true)
      setModalOpen(true)
    }
    if (assignRoleState === 'ERROR') {
      setSpeechModalOpen(false)
      setModalSuccess(false)
      setModalOpen(true)
    }
  }, [assignRoleData, assignRoleState])

  const [cancelRoleData, cancelRoleState, cancelRole] = useCancelRole()

  //Cancel Role
  const handleAttendanceCancellation = async (roleId: number | undefined) => {
    if (!clubId || !meetingTimestampUtc || !roleId) {
      console.error(
        `Missing required parameters for cancellation: clubId: ${clubId}, meetingTimestampUtc: ${meetingTimestampUtc}, roleId: ${roleId}`
      )
      return
    }
    await cancelRole(clubId, meetingTimestampUtc, roleId)
  }

  useEffect(() => {
    if (cancelRoleData) {
      setModalTitle(cancelRoleData?.title || '')
      setModalDescription(cancelRoleData?.description || '')
    }
    if (cancelRoleState === 'SUCCESS') {
      setModalSuccess(true)
      setModalOpen(true)
    }
    if (cancelRoleState === 'ERROR') {
      setModalSuccess(false)
      setModalOpen(true)
    }
  }, [cancelRoleData, cancelRoleState])

  const [cancelTarget, setCancelTarget] = useState<AssignedUser | null>(null)
  const [adminCancelMemberRole] = useClubAdminCancelMemberRoleMutation()

  const handleAdminCancellation = async (target: AssignedUser) => {
    if (!clubId || !meetingTimestampUtc) {
      return
    }
    try {
      const response = await adminCancelMemberRole({
        clubId,
        meetingTimestampUtc,
        body: { userId: target.userId, roleId: target.roleId },
      }).unwrap()
      const { title, description } = formatAdminRoleCancellationResponse(
        response,
        target.userName
      )
      showResult(true, title, description)
    } catch (error) {
      const data = (error as { data?: BaseResponse }).data
      if (data?.status) {
        const { title, description } = parseErrorResponse(data)
        showResult(false, title, description)
      } else {
        showResult(
          false,
          'Something went wrong',
          'We could not remove assignment of that role. Please try again.'
        )
      }
    }
  }

  const [updateSpeech, { isLoading: isUpdatingSpeech }] =
    useUpdateSpeechMutation()
  const [updateMemberSpeech, { isLoading: isUpdatingMemberSpeech }] =
    useClubAdminUpdateMemberSpeechMutation()

  const handleEditSpeech = (assignedUser: AssignedUser) => {
    setEditSpeechTarget(isClubAdmin ? assignedUser : null)
    setEditingSpeech({
      speechTitle: assignedUser.speechTitle ?? '',
      speechDescription: assignedUser.speechDescription ?? undefined,
      pathway: assignedUser.pathway ?? undefined,
      pathwayLevel: assignedUser.pathwayLevel ?? undefined,
    })
    setEditSpeechOpen(true)
  }

  const handleSpeechEdit = async (speechDetails: SpeechDetails) => {
    if (!clubId || !meetingTimestampUtc) {
      return
    }
    try {
      const speechTitle = speechDetails.speechTitle ?? ''
      if (editSpeechTarget) {
        await updateMemberSpeech({
          clubId,
          meetingTimestampUtc,
          body: { userId: editSpeechTarget.userId, ...speechDetails, speechTitle },
        }).unwrap()
      } else {
        await updateSpeech({
          clubId,
          meetingTimestampUtc,
          body: { ...speechDetails, speechTitle },
        }).unwrap()
      }
      setEditSpeechOpen(false)
      setEditingSpeech(null)
      setEditSpeechTarget(null)
      setModalTitle('Speech updated')
      setModalDescription(
        editSpeechTarget
          ? 'The speech details have been saved'
          : 'Your speech details have been saved'
      )
      setModalSuccess(true)
      setModalOpen(true)
    } catch {
      setEditSpeechOpen(false)
      setModalTitle('Something went wrong')
      setModalDescription(
        'We could not save the speech details. Please try again.'
      )
      setModalSuccess(false)
      setModalOpen(true)
    }
  }

  const [updateAgenda, { isLoading: isUpdatingAgenda }] =
    useUpdateMeetingAgendaMutation()

  // Save Word of the Day / Theme. Rethrow on failure so the agenda editor stays
  // open; the backend maps non-members to a 403, surfaced as a friendly message.
  const handleAgendaSave = async (body: UpdateMeetingAgendaRequest) => {
    if (!clubId || !meetingTimestampUtc) {
      return
    }
    try {
      await updateAgenda({ clubId, meetingTimestampUtc, body }).unwrap()
      setModalTitle('Meeting agenda updated')
      setModalDescription('The word of the day and theme have been saved.')
      setModalSuccess(true)
      setModalOpen(true)
    } catch (error) {
      const status = (error as { status?: number })?.status
      setModalTitle('Something went wrong')
      setModalDescription(
        status === 403
          ? 'Only club members can edit the meeting agenda.'
          : 'We could not save the meeting agenda. Please try again.'
      )
      setModalSuccess(false)
      setModalOpen(true)
      throw error
    }
  }

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setHasLoadedOnce(true) // Prevent showing spinner on dynamic page updates
    }
  }, [isLoading, isFetching])

  if (!hasLoadedOnce && (isLoading || isFetching)) {
    return <MeetingDetailsSkeleton />
  }

  if (!meetingDetails) {
    // Ensures meetingDetails is defined
    return <MeetingDetailsSkeleton />
  }

  if (!userLoaded) {
    // Ensures user is loaded
    return <MeetingDetailsSkeleton />
  }

  return (
    <div className="relative">
      <ManageClubButton
        clubId={clubId}
        isVisible={Boolean(isClubAdmin)}
        state={{ fromMeeting: meetingTimestampUtc }}
      />
      <div className="sm:px-0">
        <h3 className="text-base font-semibold leading-7 text-gray-900 mb-2">
          Time and Location
        </h3>
        <p className="max-w-2xl text-sm text-gray-500">
          {meetingDetails.address}
        </p>
        <p className="max-w-2xl text-sm text-gray-500">
          {meetingDetails.dayOfWeek}, {meetingDetails.month}{' '}
          {meetingDetails.dayOfMonth}, {meetingDetails.year}
        </p>
        <p className="max-w-2xl text-sm text-gray-500">{meetingDetails.time}</p>
      </div>
      <div className="mt-6 border-t border-b border-gray-900/10">
        <dl className="divide-y divide-gray-900/10">
          <MeetingAgendaSection
            wordOfTheDay={meetingDetails.wordOfTheDay}
            theme={meetingDetails.theme}
            canEdit={isClubMember}
            isSubmitting={isUpdatingAgenda}
            onSave={handleAgendaSave}
          />
          {meetingDetails.roles.length > 0 && isClubAdmin && (
            <AdminRoleAssignment
              clubId={clubId || ''}
              meetingTimestampUtc={meetingTimestampUtc || ''}
              roles={meetingDetails.roles}
              assignedUsers={meetingDetails.assignedUsers}
              unassignedUsers={meetingDetails.unassignedUsers}
              groups={pathwayGroups}
              onResult={showResult}
            />
          )}
          {meetingDetails.roles.length > 0 && isAuthenticated && !isClubAdmin && (
            <div className="py-6">
              <dt className="text-base font-semibold leading-6 text-gray-900">
                Choose your role
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                Choose the role you want to take on for the upcoming meeting
              </dd>
              <div className="mt-6 space-y-3 max-w-xl">
                {meetingDetails.roles.map((role) => (
                  <div
                    key={`${role.id}${role.linkedRoleId && `-${role.linkedRoleId}`}`}
                    className="grid grid-cols-3 gap-4 px-0 items-center min-h-6">
                    <label
                      htmlFor={`${role.id}`}
                      className="block text-sm text-gray-900">
                      {role.name}
                    </label>
                    {role.isAvailable &&
                      !meetingDetails.assignedUsers.some(
                        (user) =>
                          user.meetingUserEntityId === role.linkedRoleId &&
                          user.userId === currentUser?.id
                      ) && (
                      <input
                        id={`${role.id}`}
                        value={role.id}
                        name="roleId"
                        type="radio"
                        onChange={() =>
                          handleSaveRoleSelection(role.id, role.linkedRoleId)
                        }
                        className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="py-6">
            <dt className="text-base font-semibold leading-6 text-gray-900">
              Participants ({meetingDetails.assignedUsersCount})
            </dt>
            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              View the list of participants for the upcoming meeting and check
              if the role you wish to take is already occupied
            </dd>
            <div className="mt-6 space-y-2 max-w-xl">
              {meetingDetails.assignedUsers.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No participants have signed up yet.
                </p>
              ) : (
                meetingDetails.assignedUsers.map((assignedUser, index) => {
                  // Check if the name has already appeared in previous items
                  const isFirstOccurrence = meetingDetails.assignedUsers
                    .slice(0, index) // Check only users before the current one
                    .every((user) => user.userName !== assignedUser.userName)

                  return (
                    <div
                      key={`${assignedUser.userId}-${assignedUser.roleId}`}>
                      <div className="grid grid-cols-3 gap-4 px-0 items-center min-h-8">
                        <div className="block text-sm text-gray-900 mr-4">
                          {isFirstOccurrence ? assignedUser.userName : ''}
                        </div>
                        <div className="block text-sm text-gray-900 mr-4">
                          {assignedUser.roleName}
                        </div>
                        {isAuthenticated &&
                          currentUser &&
                          (isClubAdmin ||
                            currentUser.id === assignedUser.userId) && (
                            <div className="flex items-center gap-x-2">
                              <button
                                type="button"
                                onClick={async () => {
                                  if (
                                    isClubAdmin &&
                                    currentUser.id !== assignedUser.userId
                                  ) {
                                    setCancelTarget(assignedUser)
                                  } else {
                                    await handleAttendanceCancellation(
                                      assignedUser.roleId
                                    )
                                  }
                                }}
                                className="max-w-20 justify-center inline-flex items-center gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                Remove
                              </button>
                              {(isClubAdmin ||
                                currentUser.id === assignedUser.userId) &&
                                assignedUser.roleId === SPEAKER_ROLE_ID && (
                                <button
                                  type="button"
                                  onClick={() => handleEditSpeech(assignedUser)}
                                  className="max-w-20 justify-center inline-flex items-center gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                  Edit
                                </button>
                              )}
                            </div>
                          )}
                      </div>
                      {/* Speaker rows may carry speech details; legacy rows have
                          none, so render the role name alone. The details sit on
                          their own indented line below the role row. min-w-0 lets
                          the grid track shrink so long titles wrap instead of
                          overflowing past the column. */}
                      {(assignedUser.speechTitle ||
                        assignedUser.speechDescription ||
                        assignedUser.pathway ||
                        assignedUser.pathwayLevel) && (
                        <div className="grid grid-cols-3 gap-4 px-0 mt-1 mb-2">
                          <div className="hidden sm:block" />
                          <div className="col-span-3 sm:col-span-2 min-w-0 mr-4">
                            <div className="space-y-1 border-l-2 border-gray-200 pl-3 pr-20 pt-3 pb-3">
                              {assignedUser.speechTitle && (
                                <p className="break-words text-sm font-medium text-gray-900">
                                  {assignedUser.speechTitle}
                                </p>
                              )}
                              {assignedUser.speechDescription && (
                                <p className="break-words whitespace-pre-line text-sm text-gray-600">
                                  {assignedUser.speechDescription}
                                </p>
                              )}
                              {(assignedUser.pathway ||
                                assignedUser.pathwayLevel) && (
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                                  {[
                                    labelForCode(assignedUser.pathway),
                                    assignedUser.pathwayLevel
                                      ? `Level ${assignedUser.pathwayLevel}`
                                      : undefined,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </dl>
      </div>
      <div className="flex justify-center mt-6">
        <button
          type="button"
          onClick={() => navigate(`/club/${clubId}`)}
          className="block rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mr-6">
          Back
        </button>
        {/*There are roles available*/}
        {meetingDetails.roles.length > 0 && isAuthenticated && !isClubAdmin && (
          <button
            disabled={!roleSelected}
            onClick={handleRoleAssignment}
            className={`inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm ${
              roleSelected
                ? 'hover:bg-indigo-500'
                : 'opacity-50 cursor-not-allowed'
            }`}>
            Join
          </button>
        )}
        {!isAuthenticated && (
          <button
            disabled={false}
            onClick={handleRoleAssignment}
            className={`inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500`}>
            Join
          </button>
        )}
      </div>
      {/*Role Confirmation*/}
      <SimpleModalDialog
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        success={modalSuccess}
        title={modalTitle}
        description={modalDescription}
      />
      {/*Admin cancelling another participant's role*/}
      <ConfirmActionDialog
        isOpen={!!cancelTarget}
        title="Remove role assignment?"
        description={
          cancelTarget
            ? `This will remove ${cancelTarget.userName} as ${cancelTarget.roleName} for this meeting ${
                cancelTarget.roleId === SPEAKER_ROLE_ID
                  ? ' The linked Evaluator will be removed as well.'
                  : ''
              }`
            : ''
        }
        confirmText="Remove assignment"
        cancelText="Keep"
        onConfirm={async () => {
          if (cancelTarget) {
            const target = cancelTarget
            setCancelTarget(null)
            await handleAdminCancellation(target)
          }
        }}
        onCancel={() => setCancelTarget(null)}
      />
      {/*Speaker speech details*/}
      <SpeechDetailsDialog
        isOpen={speechModalOpen}
        onClose={() => setSpeechModalOpen(false)}
        onSubmit={handleSpeechSubmit}
        groups={pathwayGroups}
        isSubmitting={assignRoleState === 'PROCESSING'}
      />
      {/*Edit speaker speech details*/}
      <SpeechDetailsDialog
        isOpen={editSpeechOpen}
        onClose={() => {
          setEditSpeechOpen(false)
          setEditSpeechTarget(null)
        }}
        onSubmit={handleSpeechEdit}
        groups={pathwayGroups}
        isSubmitting={isUpdatingSpeech || isUpdatingMemberSpeech}
        initialValues={editingSpeech ?? undefined}
        title="Edit speech details"
        submitLabel="Save changes"
      />
      {/*Sign Up Dialog + Sign Up With Google*/}
      <SignUpModal
        isOpen={signUpModalOpen}
        onClose={(status: RegistrationStep) => {
          setSignUpModalOpen(false)
          setFinishSignUpModalOpen(false)
          if (status === 'COMPLETE_WITH_GOOGLE') {
            setModalSuccess(true)
            setModalOpen(true)
          }
        }}
        onTitle={(title) => setModalTitle(title)}
        onDescription={(description) => setModalDescription(description)}
        clubId={Number(clubId)}
        meetingTimestamp={Number(meetingTimestampUtc)}
      />
      {/*Finish Sign up - gets message from redux*/}
      <SimpleModalDialog
        isOpen={finishSignUpModalOpen}
        onClose={() => {
          dispatch(clearWelcomeMessage())
          setFinishSignUpModalOpen(false)
        }}
        success={true}
        title={welcomeMessage?.title}
        description={welcomeMessage?.description}
      />
    </div>
  )
}
